import React from 'react';
import DashboardSectionTile from '../../core/DashboardSectionTile';
import { Box, HStack, Text, Table, Flex, Textarea, Separator } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import {
  AddValueFields,
  EditButton,
  FieldGroup,
  InputField,
  PerUnitTdField,
  PreambleText,
  VStackLayout,
} from '../Components/index';
import { ReactComponent as WarningOutlineIconSVG } from '../../../assets/warning-outline.svg';
import './ClaimListForm.scss';
import { useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';
import { getOr } from '../../../helpers';
import moment from 'moment';
import SelectField from '../../basic/SelectField';
import { Link } from 'react-router';
import { checkIfIsAdminUser, checkIfIsGrinderUser, checkIfIsPackerUser } from '../../../utils';
import { formatMonetaryValue } from '../../../functions';
import Constants from '../../../Constants';
import { Alert } from '@/components/ui/alert';
import { Radio, RadioGroup } from '@/components/ui/radio';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import {
  PopoverRoot,
  PopoverContent,
  PopoverBody,
  PopoverTrigger,
  PopoverArrow,
  PopoverCloseTrigger,
} from '@/components/ui/popover';

const ClaimListForm = ({
  isDisabled,
  isReviewing,
  onUpdate,
  claimEntities,
  user,
  ncrs: { currentNCR },
  editDisabled,
  form,
}) => {
  const { values: formikValues, errors } = useFormikContext();
  const isEditing = get(formikValues, 'isEditing');
  const editingExistingNCR = get(formikValues, 'editingExistingNCR');
  const priority = get(currentNCR, 'claim.priority');
  const status = get(currentNCR, 'status');

  const isPackerUser = checkIfIsPackerUser(user);
  const isAdminUser = checkIfIsAdminUser(user);
  const isGrinderUser = checkIfIsGrinderUser(user);

  const adminReviewOptions = Constants.NCR_ADMIN_REVIEW_OPTIONS;

  const adminAcceptanceOptions = Constants.NCR_ADMIN_ACCEPTANCE_OPTIONS;

  const packerAcceptanceOptions = Constants.NCR_PACKER_ACCEPTANCE_OPTIONS;
  const closureOptions = Constants.NCR_ADMIN_GRINDER_CLOSURE_OPTIONS;

  const reviewOptions = isPackerUser ? packerAcceptanceOptions : adminReviewOptions;

  const acceptanceOptions = isPackerUser ? packerAcceptanceOptions : adminAcceptanceOptions;

  const showOtherField = false;
  const claimAcceptanceKey = isPackerUser ? 'packerAcceptance' : 'adminAcceptance';

  const adminReview = adminReviewOptions.find(
    rOpt => rOpt.value === get(currentNCR, 'claim.adminAcceptance.value', null)
  );

  const packerAcceptance = packerAcceptanceOptions.find(
    acceptanceOption => acceptanceOption.value === get(currentNCR, 'claim.packerAcceptance.value', null)
  );
  const packerAcceptanceDate = get(currentNCR, 'claim.packerAcceptance.modified', null);

  const adminAcceptanceDate = get(currentNCR, 'claim.adminAcceptance.modified', null);
  // const adminAcceptance = adminAcceptanceOptions.find(
  //   acceptanceOption => acceptanceOption.value === get(currentNCR, 'claim.adminAcceptance.value', null)
  // );

  const closedDate = get(currentNCR, 'closedDate', null);
  const closedByValue = closureOptions.find(
    closureOption => closureOption.value === get(currentNCR, 'closedByValue', null)
  );

  const allowFinalValueEdit = !isDisabled && isEditing && isAdminUser && editingExistingNCR;

  const ncrCurrencyCode = get(formikValues, 'ncrDetails.purchaseOrderLine.purchaseOrder.sellCurrency');

  const calculateInitialValueTotal = () =>
    claimEntities.reduce((acc, curr) => {
      const { id } = curr;
      let claimValue = get(formikValues, `claims.claim${id}.value`, 0);
      claimValue = isNaN(claimValue) ? 0 : claimValue;
      let unitValue = getOr(formikValues, `claims.claim${id}.grinderPricePerUnit`, 0);
      unitValue = isNaN(unitValue) ? 0 : unitValue;
      const measureId = get(formikValues, `claims.claim${id}.unitOfMeasureId`); // If weight then 1 and if carton then 2.
      const cartonValue = ['lbs', 'pound'].includes(formikValues?.ncrDetails?.unitOfMeasure?.name?.toLowerCase())
        ? 59.96
        : 27.2;
      return acc + (measureId && +measureId === 2 ? unitValue * cartonValue * claimValue : unitValue * claimValue);
    }, 0);

  const calculateFinalValueTotal = () =>
    claimEntities.reduce((acc, curr) => {
      const { id } = curr;
      let claimValue = get(formikValues, `claims.claim${id}.value`, 0);
      claimValue = isNaN(claimValue) ? 0 : claimValue;
      let unitValue = getOr(formikValues, `claims.claim${id}.approvedPerUnit`, 0);
      unitValue = isNaN(unitValue) ? 0 : unitValue;
      const measureId = get(formikValues, `claims.claim${id}.unitOfMeasureId`); // If weight then 1 and if carton then 2.
      const cartonValue = ['lbs', 'pound', 'pounds'].includes(
        formikValues?.ncrDetails?.unitOfMeasure?.name?.toLowerCase()
      )
        ? 59.96
        : 27.2;
      return acc + (measureId && +measureId === 2 ? unitValue * cartonValue * claimValue : unitValue * claimValue);
    }, 0);

  const purcherOrderLine = () => {
    const purchadeOrderLineId = get(formikValues, 'purchaseOrderLineId', 0);
    const lines = get(form, 'purchaseOrder.lines', []);
    const line = lines.find(l => l.id === purchadeOrderLineId);
    if (line?.sellPricePerUnit) {
      return line.sellPricePerUnit;
    }

    return null;
  };

  const claimValueComments = getOr(currentNCR, 'claim.packerAcceptance.comments', '');
  const fullHostname = window.location.hostname;

  return (
    <DashboardSectionTile title="Claim Value" padding="22px 33px 22px 20px" className="claim-list-form">
      <VStackLayout paddingBottom="42px" align="stretch" gap="24px">
        <Box>
          <Box marginBottom="24px">
            <Table.Root variant="simple">
              <Table.Header>
                <Table.Row>
                  {[
                    {
                      key: 'ncr_breakdown',
                      label: 'ncr breakdown',
                    },
                    {
                      key: 'measure',
                      label: 'measure',
                    },
                    {
                      key: 'grinder_initial_value',
                      label: 'grinder initial value',
                    },
                    {
                      key: 'approved_per_unit',
                      label: 'final value',
                    },
                  ].map(col => (
                    <Table.ColumnHeader
                      key={col.key}
                      fontSize="12px"
                      borderBottom="1px solid #b9b9b9"
                      textTransform="uppercase"
                      backgroundColor="#f7fafc"
                      fontWeight="semibold"
                    >
                      {col.label}
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body backgroundColor="#ffffff" fontSize="14px">
                {claimEntities.map(claimEntity => {
                  const { id, isRequired, name, type } = claimEntity;

                  // Determine the correct label for the claim entity
                  const displayName =
                    name === 'Other (Please specify) *FMG Approval Required' && fullHostname.includes('agilechain')
                      ? 'Other (Please specify) *CFC Approval Required'
                      : name;

                  const rowHasError = formikValues.highlightInputError && !isEmpty(get(errors, `claims.claim${id}`));
                  const claimValue = get(formikValues, `claims.claim${id}.value`);
                  let grinderPricePerUnit = get(formikValues, `claims.claim${id}.grinderPricePerUnit`, null);
                  const sellPricePerUnit = purcherOrderLine();

                  if (
                    grinderPricePerUnit === null &&
                    typeof get(formikValues, `claims.claim${id}.unitOfMeasureId`, null) !== 'object'
                  ) {
                    grinderPricePerUnit = sellPricePerUnit;
                  }

                  return (
                    <Table.Row
                      key={id}
                      fontWeight="400"
                      className={rowHasError && 'error-row'}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Table.Cell>
                        {displayName} {/* Use the modified displayName here */}
                        {isRequired ? '*' : ''}
                        {rowHasError && (
                          <PopoverRoot positioning={{ placement: 'right-end' }}>
                            <PopoverTrigger>
                              <Flex alignItems="center" color="#C62704" marginTop="8px" width="fit-content">
                                <WarningOutlineIconSVG />
                                &nbsp; Missing Information
                              </Flex>
                            </PopoverTrigger>
                            <PopoverContent
                              width="296px"
                              _focus={{
                                outline: 'none',
                              }}
                              boxShadow="0px 1.25px 4px rgba(0, 0, 0, 0.25)"
                            >
                              <PopoverArrow />
                              <PopoverCloseTrigger top="16px" right="14px" />
                              <PopoverBody padding="16px 46px 16px 19px">
                                <Flex>
                                  <WarningOutlineIconSVG />
                                  <Text marginLeft="8px" fontSize="16px" width="200px" whiteSpace="break-spaces">
                                    <b>{Object.values(getOr(errors, `claims.claim${id}`, {}))[0]}</b> is missing. Must
                                    be completed.
                                  </Text>
                                </Flex>
                              </PopoverBody>
                            </PopoverContent>
                          </PopoverRoot>
                        )}
                      </Table.Cell>
                      <Table.Cell id={`claim${id}`} paddingTop={type === 'other' ? null : '28px'}>
                        {type === 'quantity' && (
                          <Box>
                            <RadioGroup
                              key={id}
                              value={parseInt(get(formikValues, `claims.claim${id}.unitOfMeasureId`, null), 10)}
                              colorScheme="actionPrimary"
                              marginBottom="7px"
                              onValueChange={e => {
                                onUpdate(`claims.claim${id}.unitOfMeasureId`, e.value);
                              }}
                            >
                              <HStack gap="8px">
                                <Radio value={1} marginBottom={0} disabled={isDisabled || !isEditing || isPackerUser}>
                                  Weight
                                </Radio>
                                <Radio value={2} marginBottom={0} disabled={isDisabled || !isEditing || isPackerUser}>
                                  Cartons
                                </Radio>
                              </HStack>
                            </RadioGroup>
                            <InputField
                              disabled={isDisabled || !isEditing || isPackerUser}
                              value={claimValue}
                              onChange={e => {
                                onUpdate(`claims.claim${id}.value`, e.target.value);
                                onUpdate(`claims.claim${id}.grinderPricePerUnit`, grinderPricePerUnit);
                              }}
                              type="number"
                              min={0}
                            />
                          </Box>
                        )}
                        {type === 'time' && (
                          <Field label="Time (hours)">
                            <InputField
                              type="number"
                              disabled={isDisabled || !isEditing || isPackerUser}
                              value={claimValue}
                              onChange={e => onUpdate(`claims.claim${id}.value`, e.target.value)}
                            />
                          </Field>
                        )}
                        {type === 'other' && (
                          <AddValueFields
                            disabled={isDisabled || !isEditing || isPackerUser}
                            labelValue={get(formikValues, `claims.claim${id}.label`)}
                            onClaimLabelChange={e => {
                              onUpdate(`claims.claim${id}.label`, e.target.value);
                            }}
                            claimValue={claimValue}
                            onClaimValueChange={e => {
                              onUpdate(`claims.claim${id}.value`, e.target.value);
                            }}
                          />
                        )}
                      </Table.Cell>
                      <PerUnitTdField
                        disabled={isDisabled || !isEditing || isPackerUser}
                        value={grinderPricePerUnit}
                        onChange={e => {
                          onUpdate(`claims.claim${id}.grinderPricePerUnit`, e.target.value);
                        }}
                      />
                      <PerUnitTdField
                        disabled={!allowFinalValueEdit}
                        value={get(formikValues, `claims.claim${id}.approvedPerUnit`, null)}
                        onChange={e => {
                          onUpdate(`claims.claim${id}.approvedPerUnit`, e.target.value);
                        }}
                      />
                    </Table.Row>
                  );
                })}
                {showOtherField && (
                  <>
                    <Table.Row fontWeight="400">
                      <Table.Cell>Destruction Costs</Table.Cell>
                      <Table.Cell>
                        <AddValueFields disabled={isDisabled} />
                      </Table.Cell>
                      <PerUnitTdField disabled={isDisabled} />
                      <PerUnitTdField disabled={isDisabled} />
                    </Table.Row>
                    <Table.Row fontWeight="400">
                      <Table.Cell>Transport Costs</Table.Cell>
                      <Table.Cell>
                        <AddValueFields disabled={isDisabled} />
                      </Table.Cell>
                      <PerUnitTdField disabled={isDisabled} />
                      <PerUnitTdField disabled={isDisabled} />
                    </Table.Row>
                    <Table.Row fontWeight="400">
                      <Table.Cell>Costs Covered</Table.Cell>
                      <Table.Cell>
                        <AddValueFields disabled={isDisabled} />
                      </Table.Cell>
                      <PerUnitTdField disabled={isDisabled} />
                      <PerUnitTdField disabled={isDisabled} />
                    </Table.Row>
                  </>
                )}
              </Table.Body>

              <Table.Footer>
                <Table.Row textTransform="uppercase" backgroundColor="#f7fafc" fontWeight="semibold">
                  <Table.Cell>Total</Table.Cell>
                  <Table.Cell />
                  <Table.Cell>
                    {formatMonetaryValue(ncrCurrencyCode, calculateInitialValueTotal(), {
                      notation: 'standard',
                    }) || '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {formatMonetaryValue(ncrCurrencyCode, calculateFinalValueTotal(), {
                      notation: 'standard',
                    }) || '-'}
                  </Table.Cell>
                </Table.Row>
              </Table.Footer>
            </Table.Root>
          </Box>
          {!isReviewing && isGrinderUser && (
            <Box>
              <Checkbox
                colorScheme="actionPrimary"
                checked={get(formikValues, 'claim.isUnderReview', null)}
                onChange={e => onUpdate('claim.isUnderReview', e.target.checked)}
                disabled={
                  isDisabled || (get(formikValues, 'recordNo') !== undefined && status !== Constants.NCR_STATUES.RAISED)
                }
              >
                Claim Value Data under Review by Grinder
              </Checkbox>
              {get(formikValues, 'claim.isUnderReview') && (
                <Alert
                  status="info"
                  marginTop="19px"
                  // variant="left-accent"
                  borderRadius="6px"
                  fontWeight="400"
                  fontSize="16px"
                  paddingY="16px"
                  width="fit-content"
                  paddingRight="50px"
                >
                  <b>Important:</b> If you want to complete other information please do it before&nbsp;
                  <b>{moment().add(7, 'days').format('DD-MM-YYYY')}</b>
                </Alert>
              )}
            </Box>
          )}
        </Box>
        {isReviewing ? (
          <HStack gap="31px" alignItems="flex-start">
            <Box>
              {priority === 'serious' && (
                <Box marginBottom="38px">
                  <PreambleText fontSize="16px">
                    Claim Value Review by {fullHostname.includes('agilechain') ? 'CFC' : 'FMG'}
                  </PreambleText>

                  {adminReview ? (
                    <Text>
                      <b>{get(adminReview, 'label', null)}</b>
                      &nbsp;by&nbsp;
                      {get(currentNCR, 'claim.adminAcceptance.user')
                        ? `${get(currentNCR, 'claim.adminAcceptance.user.firstName')} ${get(
                            currentNCR,
                            'claim.adminAcceptance.user.lastName'
                          )}`
                        : '-'}
                      &nbsp;on&nbsp;
                      {moment(adminAcceptanceDate).isValid() ? moment(adminAcceptanceDate).format('DD-MMM-YYYY') : '-'}
                    </Text>
                  ) : (
                    '-'
                  )}
                </Box>
              )}
              <Box>
                <PreambleText fontSize="16px">NCR Acceptance including Claim Value by Packer</PreambleText>
                {packerAcceptance ? (
                  <Text>
                    <b>{get(packerAcceptance, 'label', null)}</b>
                    &nbsp;by&nbsp;
                    {get(currentNCR, 'claim.packerAcceptance.user')
                      ? `${get(currentNCR, 'claim.packerAcceptance.user.firstName')} ${get(
                          currentNCR,
                          'claim.packerAcceptance.user.lastName'
                        )}`
                      : '-'}
                    &nbsp;on&nbsp;
                    {moment(packerAcceptanceDate).isValid() ? moment(packerAcceptanceDate).format('DD-MMM-YYYY') : '-'}
                  </Text>
                ) : (
                  '-'
                )}
              </Box>
            </Box>
            <Separator orientation="vertical" borderWidth="1px" height="10" />
            {priority === 'major' && (
              <Box>
                <PreambleText fontSize="16px">
                  NCR Closed by {fullHostname.includes('agilechain') ? 'CFC' : 'FMG'} or Grinder
                </PreambleText>

                {closedByValue ? (
                  <Text>
                    <b>Closed</b>
                    &nbsp;by&nbsp;
                    {get(currentNCR, 'closedByUser')
                      ? `${get(currentNCR, 'closedByUser.firstName')} ${get(currentNCR, 'closedByUser.lastName')}`
                      : '-'}
                    &nbsp;on&nbsp;
                    {moment(closedDate).isValid() ? moment(closedDate).format('DD-MMM-YYYY') : '-'}
                  </Text>
                ) : (
                  '-'
                )}
              </Box>
            )}
            {priority === 'serious' && (
              <Box>
                <PreambleText fontSize="16px">
                  Final NCR Closed by {fullHostname.includes('agilechain') ? 'CFC' : 'FMG'}
                </PreambleText>

                {closedByValue ? (
                  <Text>
                    <b>Closed</b>
                    &nbsp;by&nbsp;
                    {get(currentNCR, 'closedByUser')
                      ? `${get(currentNCR, 'closedByUser.firstName')} ${get(currentNCR, 'closedByUser.lastName')}`
                      : '-'}
                    &nbsp;on&nbsp;
                    {moment(closedDate).isValid() ? moment(closedDate).format('DD-MMM-YYYY') : '-'}
                  </Text>
                ) : (
                  '-'
                )}
              </Box>
            )}
            {isReviewing && (isPackerUser || (priority === 'serious' && isAdminUser) || priority === 'major') ? (
              <Box marginLeft="auto !important">
                <Link to={`/edit-ncr/${get(currentNCR, 'recordNo')}?startEditTabKey=claim_value`}>
                  <EditButton disabled={editDisabled} />
                </Link>
              </Box>
            ) : (
              ''
            )}
          </HStack>
        ) : (
          ''
        )}
        {isReviewing && (isAdminUser || isPackerUser) && claimValueComments ? (
          <Box>
            <PreambleText fontSize="16px">Comments:</PreambleText>
            <>
              <Text>
                <b>
                  {get(currentNCR, 'claim.packerAcceptance.user')
                    ? `${get(currentNCR, 'claim.packerAcceptance.user.firstName')} ${get(
                        currentNCR,
                        'claim.packerAcceptance.user.lastName'
                      )}`
                    : '-'}
                </b>
                &nbsp;&nbsp;
                <Box as="span" opacity="0.5">
                  {moment(packerAcceptanceDate).isValid()
                    ? moment(packerAcceptanceDate).format('MMMM D, YYYY, hh:mm A')
                    : ''}
                </Box>
              </Text>
              <Text>{getOr(currentNCR, 'claim.packerAcceptance.comments', '--')}</Text>
            </>
          </Box>
        ) : (
          ''
        )}
        {!isReviewing && editingExistingNCR ? (
          <Box>
            <HStack gap="70px">
              {isPackerUser || (isAdminUser && priority === 'serious') ? (
                <FieldGroup
                  label={
                    isPackerUser
                      ? 'NCR Acceptance including Claim Value by Packer'
                      : `Claim Value Review by ${fullHostname.includes('agilechain') ? 'CFC' : 'FMG'}`
                  }
                  labelProps={{
                    width: 'max-content',
                  }}
                >
                  <SelectField
                    placeholder="Select"
                    name="claimAcceptance"
                    value={reviewOptions.filter(
                      reviewOption =>
                        getOr(
                          formikValues,
                          `claim.${claimAcceptanceKey}.value.value`,
                          getOr(formikValues, `claim.${claimAcceptanceKey}.value`, null)
                        ) === reviewOption.value
                    )}
                    className={
                      formikValues.highlightInputError && get(errors, `claim.${claimAcceptanceKey}`) && 'error'
                    }
                    onChange={e => onUpdate(`claim.${claimAcceptanceKey}.value`, e)}
                    options={reviewOptions}
                  />
                </FieldGroup>
              ) : (
                ''
              )}
              {(isAdminUser || isGrinderUser) && priority === 'major' ? (
                <FieldGroup label={`NCR Closed by ${fullHostname.includes('agilechain') ? 'CFC' : 'FMG'} or Grinder`}>
                  <SelectField
                    placeholder="Select"
                    name="claimAcceptance"
                    value={closureOptions.filter(
                      closureOption =>
                        getOr(formikValues, 'closedByValue.value', getOr(formikValues, 'closedByValue', null)) ===
                        closureOption.value
                    )}
                    onChange={e => onUpdate('closedByValue', e)}
                    options={closureOptions}
                    disabled={!packerAcceptance}
                  />
                </FieldGroup>
              ) : (
                ''
              )}
              {isAdminUser && priority === 'serious' ? (
                <FieldGroup label={`Final NCR Closed by ${fullHostname.includes('agilechain') ? 'CFC' : 'FMG'}`}>
                  <SelectField
                    placeholder="Select"
                    name="closureAcceptance"
                    value={acceptanceOptions.filter(
                      acceptanceOption =>
                        getOr(formikValues, 'closedByValue.value', getOr(formikValues, 'closedByValue', null)) ===
                        acceptanceOption.value
                    )}
                    className={formikValues.highlightInputError && get(errors, 'closedByValue') && 'error'}
                    onChange={e => onUpdate('closedByValue', e)}
                    options={acceptanceOptions}
                    disabled={!packerAcceptance}
                  />
                </FieldGroup>
              ) : (
                ''
              )}
            </HStack>
            {isPackerUser && (
              <Box marginTop="20px">
                <Field label="Comments:">
                  <Textarea
                    rows={6}
                    placeholder="Comments"
                    name="comments"
                    value={get(formikValues, `claim.${claimAcceptanceKey}.comments`, null)}
                    className={
                      formikValues.highlightInputError && get(errors, `claim.${claimAcceptanceKey}.comments`) && 'error'
                    }
                    onChange={e => onUpdate(`claim.${claimAcceptanceKey}.comments`, e.target.value)}
                  />
                </Field>
              </Box>
            )}
          </Box>
        ) : (
          ''
        )}
      </VStackLayout>
    </DashboardSectionTile>
  );
};

ClaimListForm.propTypes = {
  isDisabled: PropTypes.bool,
  isReviewing: PropTypes.bool,
  onUpdate: PropTypes.func,
  claimEntities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      isRequired: PropTypes.bool,
      name: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
  }),
  ncrs: PropTypes.shape({
    currentNCR: PropTypes.shape({
      claim: PropTypes.shape({
        priority: PropTypes.string,
        adminAcceptance: PropTypes.shape({
          value: PropTypes.string,
          modified: PropTypes.string,
          user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
          }),
        }),
        packerAcceptance: PropTypes.shape({
          value: PropTypes.string,
          modified: PropTypes.string,
          user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
          }),
          comments: PropTypes.string,
        }),
      }),
      status: PropTypes.string,
      closedDate: PropTypes.string,
      closedByValue: PropTypes.string,
      closedByUser: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
  }),
  editDisabled: PropTypes.bool,
  form: PropTypes.shape({
    purchaseOrder: PropTypes.shape({
      lines: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          sellPricePerUnit: PropTypes.number,
        })
      ),
    }),
  }),
};

export default ClaimListForm;
